import { Button, DatePicker, Icon, Popover, Spin } from 'antd';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { getSchedulePeriod } from '../../model/workSchedulePeriod';
import { ScheduleItem } from './ScheduleItem/ScheduleItem';
import styles from './Schedules.module.scss';
import { ReactComponent as FiltrationRegistrationIcon } from '../../../../../../resource/FiltrationRegistration.svg';

const { MonthPicker } = DatePicker;
const filterDisplayDateFormat = 'MMMM YYYY';
const getSchedulePeriodText = (schedule) => {
  const period = getSchedulePeriod(schedule);

  if (!period) {
    return '';
  }

  const startMonthText = period.startMonth.format(filterDisplayDateFormat);
  const endMonthText = period.endMonth.format(filterDisplayDateFormat);

  return period.startMonth.isSame(period.endMonth, 'month')
    ? startMonthText
    : `${startMonthText} — ${endMonthText}`;
};

const scheduleMatchesMonth = (schedule, month) => {
  if (!month) {
    return true;
  }

  const period = getSchedulePeriod(schedule);

  if (!period) {
    return false;
  }

  return (
    period.startMonth.isSameOrBefore(month, 'month') &&
    period.endMonth.isSameOrAfter(month, 'month')
  );
};

export const Schedules = ({
  schedules = [],
  onSelectSchedule,
  selectedSchedule,
  showScheduleDetails = true,
  closeOverlaysKey,
  onEditSchedule,
  onDuplicateSchedule,
  onDeleteSchedule,
  onActionPopoverVisibleChange,
  showPeriodFilter = false,
  isLoading = false,
}) => {
  const [filterMonth, setFilterMonth] = useState(null);
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const previousSelectedIdRef = useRef(null);
  const filteredSchedules = useMemo(
    () =>
      showPeriodFilter
        ? schedules.filter((schedule) =>
            scheduleMatchesMonth(schedule, filterMonth)
          )
        : schedules,
    [filterMonth, schedules, showPeriodFilter]
  );

  const handleFilterChange = (month) => {
    setFilterMonth(month);
    setIsFilterVisible(false);
  };

  const handlePrevMonth = () => {
    setFilterMonth((prevMonth) =>
      (prevMonth || moment()).clone().subtract(1, 'month')
    );
  };

  const handleNextMonth = () => {
    setFilterMonth((prevMonth) =>
      (prevMonth || moment()).clone().add(1, 'month')
    );
  };

  const handleResetFilter = () => {
    setFilterMonth(null);
    setIsFilterVisible(false);
  };

  useEffect(() => {
    const selectedId = selectedSchedule?.id ?? null;
    if (previousSelectedIdRef.current !== selectedId) {
      previousSelectedIdRef.current = selectedId;
      if (
        showPeriodFilter &&
        selectedSchedule &&
        !scheduleMatchesMonth(selectedSchedule, filterMonth)
      ) {
        setFilterMonth(getSchedulePeriod(selectedSchedule)?.startMonth || null);
      }
      return;
    }

    if (
      !isLoading &&
      showPeriodFilter &&
      selectedSchedule &&
      typeof onSelectSchedule === 'function' &&
      !filteredSchedules.some((schedule) => schedule.id === selectedSchedule.id)
    ) {
      onSelectSchedule(null);
    }
  }, [
    filterMonth,
    filteredSchedules,
    isLoading,
    onSelectSchedule,
    selectedSchedule,
    showPeriodFilter,
  ]);

  useEffect(() => {
    if (!showPeriodFilter && filterMonth) {
      setFilterMonth(null);
      setIsFilterVisible(false);
    }
  }, [filterMonth, showPeriodFilter]);

  useEffect(() => {
    setIsFilterVisible(false);
  }, [closeOverlaysKey]);

  const filterContent = (
    <div className={styles.filterPopover}>
      <MonthPicker
        autoFocus
        format={filterDisplayDateFormat}
        placeholder="Месяц и год"
        value={filterMonth}
        onChange={handleFilterChange}
      />
      <Button
        className={styles.resetFilterButton}
        disabled={!filterMonth}
        onClick={handleResetFilter}
      >
        Сбросить
      </Button>
    </div>
  );

  return (
    <div className={styles.root}>
      {showPeriodFilter && (
        <div className={styles.periodFilter}>
          <div className={styles.periodFilterDate}>
            <Button
              className={styles.periodFilterNavButton}
              icon="left"
              onClick={handlePrevMonth}
            />
            <span className={styles.periodFilterText}>
              {filterMonth ? filterMonth.format('MMMM YYYY') : 'Все периоды'}
            </span>
            <Button
              className={styles.periodFilterNavButton}
              icon="right"
              onClick={handleNextMonth}
            />
          </div>
          <Popover
            trigger="click"
            placement="bottomRight"
            visible={isFilterVisible}
            onVisibleChange={setIsFilterVisible}
            content={filterContent}
          >
            <Button
              className={cn(styles.button, styles.button__settings, {
                [styles.button__active]: Boolean(filterMonth),
              })}
            >
              <Icon component={FiltrationRegistrationIcon} />
            </Button>
          </Popover>
        </div>
      )}
      <div>
        {isLoading ? (
          <div className={styles.listState}>
            <Spin />
          </div>
        ) : (
          <ul className={styles.scheduleList}>
            {filteredSchedules.map((schedule) => (
              <ScheduleItem
                key={schedule.id}
                schedule={schedule}
                onSelect={onSelectSchedule}
                isSelected={selectedSchedule?.id === schedule.id}
                departmentName={schedule.departmentName}
                periodText={getSchedulePeriodText(schedule)}
                quantityOfWorkers={schedule.quantityOfWorkers}
                showDetails={showScheduleDetails}
                showActions={!schedule.isDraft}
                closeOverlaysKey={closeOverlaysKey}
                onActionPopoverVisibleChange={onActionPopoverVisibleChange}
                onEdit={onEditSchedule}
                onDuplicate={onDuplicateSchedule}
                onDelete={onDeleteSchedule}
              />
            ))}
          </ul>
        )}
        {showPeriodFilter && !filteredSchedules.length && (
          <div className={styles.emptyFilter}>
            Нет графиков за выбранный месяц
          </div>
        )}
      </div>
    </div>
  );
};
